import React, { Fragment, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import styles from './autocomplete.module.css';
import { twMerge } from 'tailwind-merge';
import useOnClickOutside from '../../hooks/useOnClickOutside';

type Option =
  | {
      id: string;
      name: string;
    }
  | { id: string; name: string; [key: string]: any }
  | string
  | any;

interface IAutocompleteProps {
  displayOption?: (props: { selected: boolean; active: boolean; option: Option }) => ReactNode;
  displayValue?: (props: Option) => any;
  onChange: (option: Option) => void;
  options: Option[];
  CaretIcon?: ReactNode;
  className?: string;
  value?: string | Option;
  TitleComponent?: ReactNode;
  title?: string;
  disabled?: boolean;
  onInput?: (value: string) => void;
  inputPrepend?: ReactNode;
}

const defaultDisplayOption = ({ selected, active, option }: { selected: boolean; active: boolean; option: Option }) => (
  <>
    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{option?.name ?? option}</span>
    {selected ? <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-teal-600'}`}></span> : null}
  </>
);

export default function Autocomplete(props: IAutocompleteProps) {
  const [selected, setSelected] = useState<any>(props.value);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const comboboxRef = useRef(null)
  
  const { options, disabled, value, displayOption = defaultDisplayOption, onInput, displayValue = props => props?.name ?? props ?? '', onChange } = props;

  const handleChange = useCallback(
    (option: Option) => {
      setSelected(option);
      onChange(option);
      setOpen(false);
    },
    [onChange]
  );

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (typeof onInput === 'function') {
        onInput(event.target.value);
      }
      setQuery(event.target.value);
    },
    [onInput, value]
  );

  useEffect(() => {
    const option = options?.find(option => (option?.id === value?.id && option?.name === value?.name) || option?.name === value?.name);

    if (option) {
      setSelected(option);
    } else {
      setSelected(value);
    }
  }, [value, options]);

  useOnClickOutside([comboboxRef], () => {
    setOpen(false)
  })

  const filteredList =
    query === '' && open
      ? [...options]
      : [...options]?.filter(option =>
          option?.name
            ? option?.name?.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, ''))
            : option?.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  return (
    <Combobox nullable disabled={disabled} value={selected} onChange={handleChange} ref={comboboxRef}>
      <div className={twMerge('auto-complete relative', props.className ?? '')}>
        <div className={styles['input-group']}>
          <Combobox.Input
            autoComplete="off"
            style={{ width: '100%' }}
            className={styles.input}
            onFocus={(e) => e.target.value === "" && setOpen(true)}
            onInput={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onChange={handleInput}
            displayValue={displayValue}
          />
          {props.inputPrepend && (
            <div
              onClick={() => setOpen(prev => !prev)}
              className="

              absolute right-2 top-1/2 -translate-y-1/2 flex items-center z-10 justify-center cursor-pointer w-8 h-8 text-sm text-gray-500
          "
            >
              {props.inputPrepend}
            </div>
          )}
        </div>
        <Transition
          show={open && filteredList?.length > 0}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          // afterLeave={() => setQuery('')}
        >
          <Combobox.Options className={styles['option-list'] + ' w-full'}>
            {filteredList?.map(option => (
              <Combobox.Option
                key={option.id ?? option?.name ?? option}
                className={({ active }) => `${styles.option} ${active ? 'bg-secondary2' : 'bg-white'}`}
                value={option}
              >
                {({ selected, active }) => <>{displayOption({ selected, active, option })}</>}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}
